import { createElement, useCallback, useState } from "react";

import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";

import {
  isPublishOutcomeUnknown,
  websiteService,
  type PreflightError,
} from "../../api/websiteService";
import { preflightErrorText, preflightWarningText } from "../components/host/hostLabels";
import type { DraftEditor } from "./useDraftEditor";

const errMessage = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** 422 的 details 可能是字符串,也可能是 `{token, page, reason}`(A.50);统一成 PreflightError。 */
export function detailsAsPreflightErrors(e: unknown): PreflightError[] {
  const d = (e as { details?: unknown } | null | undefined)?.details;
  if (!Array.isArray(d)) return [];
  return d.flatMap((x): PreflightError[] => {
    if (typeof x === "string") return [{ reason: x }];
    if (x && typeof x === "object" && typeof (x as PreflightError).reason === "string") {
      return [x as PreflightError];
    }
    return [];
  });
}

const lines = (items: string[]) =>
  createElement(
    "span",
    { className: "block max-h-[50dvh] overflow-y-auto whitespace-pre-line" },
    items.join("\n")
  );

/**
 * host 模式的发布(A.50 / A.51 / A.57):
 *  ① 有未保存改动先存(体检读的是库里的草稿,不存就体检了旧的);
 *  ② `GET publish/preflight`:`errors` 非空 → 阻断,错误列表交给页面(`blockers`)逐条画,可点定位到节点;
 *  ③ 确认框:有 `warnings` 标题换成「以下模板自带图片 / 外链会被清空,继续发布?」并逐条列出;
 *  ④ `POST publish`;回包 `warnings` 也亮出来;422 带 details 的当体检错误画回页面;409「结果未知」中性提示(同 stub)。
 * 发布之后(成功 / 409)重拉站点让版本号走到下一位。
 */
export function useHostPublish(ed: DraftEditor) {
  const confirm = useConfirm();
  const [publishing, setPublishing] = useState(false);
  const [blockers, setBlockers] = useState<PreflightError[]>([]);
  const { site, dirty, persist, refreshSite } = ed;

  const publish = useCallback(async () => {
    if (!site) return;
    const id = site.id;
    const next = (site.currentVersionNo ?? 0) + 1;
    setPublishing(true);
    try {
      if (dirty && !(await persist())) return;
      let pre;
      try {
        pre = await websiteService.preflight(id);
      } catch (e) {
        toast({ title: "发布前检查失败", description: errMessage(e), variant: "destructive" });
        return;
      }
      setBlockers(pre.errors);
      if (pre.errors.length > 0) {
        toast({
          title: `有 ${pre.errors.length} 处要先改,才能发布`,
          description: pre.errors.map(preflightErrorText).join("\n"),
          variant: "destructive",
        });
        return;
      }
      const warnings = pre.warnings.map(preflightWarningText);
      const ok = await confirm(
        warnings.length > 0
          ? {
              title: "以下模板自带图片 / 外链会被清空,继续发布?",
              description: lines([...warnings, "", `确认后以当前草稿生成 v${next} 并发布到宿主。`]),
              confirmText: "继续发布",
              variant: "warning",
            }
          : {
              title: `发布 v${next}?`,
              description: `以当前草稿生成 v${next} 并发布到宿主。`,
              confirmText: "发布",
            }
      );
      if (!ok) return;
      const v = await websiteService.publish(id);
      const done = (v.warnings ?? []).map(preflightWarningText);
      toast({
        title: `已发布 v${v.versionNo}`,
        description: lines([v.previewUrl ?? site.previewUrl, ...done]),
      });
      await refreshSite(id);
    } catch (e) {
      if (isPublishOutcomeUnknown(e)) {
        toast({ title: "发布结果未知,稍后看版本", description: errMessage(e) });
        await refreshSite(id);
        return;
      }
      const status = (e as { httpStatus?: unknown } | null)?.httpStatus;
      const details = status === 422 ? detailsAsPreflightErrors(e) : [];
      if (details.length > 0) setBlockers(details);
      toast({
        title: status === 422 ? "发布前校验没过" : "发布失败",
        description: details.length ? details.map(preflightErrorText).join("\n") : errMessage(e),
        variant: "destructive",
      });
      if (status === 409) await refreshSite(id);
    } finally {
      setPublishing(false);
    }
  }, [site, dirty, persist, refreshSite, confirm]);

  return { publish, publishing, blockers, clearBlockers: () => setBlockers([]) };
}
